import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminPage, AdminPageHeader } from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ArtistModerationCase, artistModerationList, artistModerationResolve } from "@/services/firebase/artistHub";
import {
  ArtistModerationDecision,
  ArtistModerationStatus,
  ArtistModerationType,
  DECISION_HELP,
  DECISION_LABELS,
  RESOLVE_NOTE_MAX,
  isOverdue,
  needsConfirm,
  noteValid,
  reasonLabel,
  reasonSummary,
  typeLabel,
  waitedHours,
  waitedLabel,
} from "@/lib/moderation";
import { useArtistConfirm } from "@/pages/actors/ArtistConfirm";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const errorText = (e: unknown) => (e instanceof Error ? e.message : "操作未完成，請重試");
const typeFilters: { value: ArtistModerationType | null; label: string }[] = [
  { value: null, label: "全部" },
  { value: "content", label: "動態" },
  { value: "comment", label: "留言" },
];

/**
 * Q-02 動態檢舉（Apple Guideline 1.2；contract §4「v3.4 補充」）. Staff only.
 *   moderationList {status, type?} → open: oldest first (24 h SLA, red past 24 h); resolved: newest first
 *   moderationResolve {id, decision, note} — decisions come from each row's allowedDecisions; the note is required.
 * New reports also reach Slack immediately (artistModerationAlert) and in the daily report.
 */
export default function ArtistModerationPage() {
  const { confirm, dialog } = useArtistConfirm();
  const client = useQueryClient();
  const [status, setStatus] = useState<ArtistModerationStatus>("open");
  const [type, setType] = useState<ArtistModerationType | null>(null);
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const cursor = pages[pages.length - 1];
  const list = useQuery({
    queryKey: ["artist-moderation", status, type, cursor],
    queryFn: () => artistModerationList({ status, type, cursor }),
    retry: false,
    refetchInterval: status === "open" ? 60000 : false,
  });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState("");
  const resolve = useMutation({
    mutationFn: ({ c, decision }: { c: ArtistModerationCase; decision: ArtistModerationDecision }) =>
      artistModerationResolve(c.id, decision, notes[c.id] ?? ""),
    onSuccess: (r) => {
      setNotice(`已處理 ${r.id}：${r.resolution?.decision ? DECISION_LABELS[r.resolution.decision] : "完成"}`);
      void client.invalidateQueries({ queryKey: ["artist-moderation"] });
    },
  });
  const now = Date.now();
  const items = list.data?.items ?? [];
  const reset = () => setPages([null]);

  return (
    <AdminPage maxWidth="wide">
      <AdminPageHeader
        title="動態檢舉"
        subtitle="星空動態與留言的使用者檢舉；24 小時內處理，處置會寫入稽核紀錄"
        loading={list.isFetching}
        onRefresh={() => void list.refetch()}
      />
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>
            檢舉佇列
            {status === "open" && list.data?.openCount !== undefined && (
              <span className="ml-3 text-sm font-normal text-muted-foreground">
                待處理 {list.data.openCount} 件
                {(list.data.overdueCount ?? 0) > 0 && <span className="ml-2 font-semibold text-destructive">超過 24 小時 {list.data.overdueCount} 件</span>}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex gap-2" role="tablist" aria-label="狀態">
              {(["open", "resolved"] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  role="tab"
                  variant={status === s ? "default" : "outline"}
                  aria-selected={status === s}
                  onClick={() => {
                    setStatus(s);
                    reset();
                  }}
                >
                  {s === "open" ? "待處理" : "已處理"}
                </Button>
              ))}
            </div>
            <div className="flex gap-2" role="group" aria-label="類型">
              {typeFilters.map((f) => (
                <Button
                  key={f.label}
                  size="sm"
                  variant={type === f.value ? "secondary" : "ghost"}
                  aria-pressed={type === f.value}
                  onClick={() => {
                    setType(f.value);
                    reset();
                  }}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
          {(list.isError || resolve.isError) && (
            <p role="alert" className="text-destructive">
              {errorText(resolve.error ?? list.error)}
            </p>
          )}
          {notice && <p role="status">{notice}</p>}
          {list.isPending && <p>讀取檢舉…</p>}
          {list.data && items.length === 0 && (
            <p className="text-sm text-muted-foreground">{status === "open" ? "目前沒有待處理的檢舉。" : "沒有已處理的檢舉。"}</p>
          )}
          {items.length > 0 && (
            <ul className="divide-y">
              {items.map((c) => (
                <CaseRow
                  key={c.id}
                  c={c}
                  now={now}
                  note={notes[c.id] ?? ""}
                  onNote={(v) => setNotes((n) => ({ ...n, [c.id]: v }))}
                  busy={resolve.isPending}
                  onDecide={(decision) => {
                    if (!needsConfirm(decision)) resolve.mutate({ c, decision });
                    else
                      confirm({
                        title: `確定「${DECISION_LABELS[decision]}」？`,
                        message: DECISION_HELP[decision],
                        confirmText: DECISION_LABELS[decision],
                        danger: true,
                        onConfirm: () => resolve.mutate({ c, decision }),
                      });
                  }}
                />
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {pages.length > 1 && (
              <Button size="sm" variant="outline" disabled={list.isFetching} onClick={reset}>
                回到第一頁
              </Button>
            )}
            {list.data?.nextCursor && (
              <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => setPages((p) => [...p, list.data?.nextCursor ?? null])}>
                下一頁
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      {dialog}
    </AdminPage>
  );
}

function CaseRow({
  c,
  now,
  note,
  onNote,
  busy,
  onDecide,
}: {
  c: ArtistModerationCase;
  now: number;
  note: string;
  onNote: (v: string) => void;
  busy: boolean;
  onDecide: (d: ArtistModerationDecision) => void;
}) {
  const s = c.snapshot;
  const open = c.status === "open";
  const hours = waitedHours(c.openedAt, now);
  const late = open && (c.overdue || isOverdue(c.openedAt, now));
  const noteId = `mod-note-${c.id}`;
  return (
    <li className={`space-y-2 py-3 ${late ? "border-l-4 border-l-destructive pl-3" : ""}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant={c.type === "comment" ? "info" : "warning"}>{typeLabel(c.type, s.contentType)}</Badge>
        <Badge variant="danger">{reasonLabel(c.lastReason)}</Badge>
        <span>檢舉 {c.reportCount} 人</span>
        {c.reportCount > 1 && <span className="text-xs text-muted-foreground">（{reasonSummary(c.reasons)}）</span>}
        <span className="text-muted-foreground">藝人 {s.artistName ?? c.artistId ?? "—"}</span>
        {open ? (
          <span className={late ? "font-semibold text-destructive" : "text-muted-foreground"}>
            {late ? "⚠ 已超過 24 小時・" : ""}等待 {waitedLabel(hours)}
          </span>
        ) : (
          <Badge variant="success">
            {c.resolution?.decision ? DECISION_LABELS[c.resolution.decision] : "已處理"} · {fmt(c.resolvedAt)}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">首次檢舉 {fmt(c.createdAt)}</span>
      </div>
      <div className="flex gap-3">
        {c.type === "content" && s.previewUrl && (
          <img src={s.previewUrl} alt="動態縮圖" className="h-20 w-20 flex-none rounded object-cover" loading="lazy" referrerPolicy="no-referrer" />
        )}
        <div className="min-w-0 flex-1 rounded bg-muted p-2 text-sm">
          {c.type === "comment" ? (
            <p className="mb-1 text-xs text-muted-foreground">
              {s.authorKind === "artist" ? "藝人團隊" : "粉絲"}「{s.authorName ?? "—"}」{s.parentId ? "的回覆" : "的留言"}・在「{s.title ?? "（無標題）"}」底下
            </p>
          ) : (
            s.title && <p className="mb-1 font-medium">{s.title}</p>
          )}
          <p className="whitespace-pre-wrap break-words">{s.text || "（無文字內容）"}</p>
        </div>
      </div>
      {c.recentReports.some((r) => r.note) && (
        <ul className="list-disc pl-5 text-xs text-muted-foreground">
          {c.recentReports
            .filter((r) => r.note)
            .map((r, i) => (
              <li key={i}>
                {reasonLabel(r.reason)}：{r.note}（{fmt(r.createdAt)}）
              </li>
            ))}
        </ul>
      )}
      {!open && c.resolution?.note && <p className="text-sm">處理備註：{c.resolution.note}</p>}
      {open && (
        <div className="space-y-2">
          <div>
            <Label htmlFor={noteId}>處理備註（必填，≤{RESOLVE_NOTE_MAX} 字）</Label>
            <textarea
              id={noteId}
              className="mt-1 block w-full rounded border bg-background px-2 py-1 text-sm"
              rows={2}
              maxLength={RESOLVE_NOTE_MAX}
              value={note}
              onChange={(e) => onNote(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {c.allowedDecisions.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={d === "dismiss" ? "outline" : "destructive"}
                disabled={busy || !noteValid(note)}
                title={DECISION_HELP[d]}
                onClick={() => onDecide(d)}
              >
                {DECISION_LABELS[d]}
              </Button>
            ))}
            {!noteValid(note) && <span className="text-xs text-muted-foreground">填寫備註後才能處置</span>}
          </div>
        </div>
      )}
    </li>
  );
}
